import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { useSelector, useDispatch } from "react-redux";
import API from "../../../util/api";
import { actions } from "../../../util/actions";
import ConfirmationModal from "../../Common/ConfirmationModal";
import { showErrorModal } from "../../../util/general";

const Container = styled.div`
  background: #F8F8F8;
  border-radius: 5px;
  box-shadow: 2px 5px 5px rgba(0,0,0,0.05);
  padding: 1em;
  max-width: 1000px;
  margin: auto;
`;

const SectionTitle = styled.h1`
  font-size: 24px;
  margin-bottom: 0.5em;
  border-top: 2px solid #ccc;
  border-bottom: 2px solid #ccc;
  background-color: #fff;
  padding: 1em;
  font-weight: normal;
  color: ${({ colors }) => colors.labelText};
`;

const FieldRow = styled.div`
  display: flex;
  gap: 1em;
  margin-bottom: 1em;
  & > div { flex: 1; }
`;

const Field = styled.div`
  margin-left: 0.1em;
  label {
    display: block;
    margin-bottom: 0.3em;
    font-weight: 600;
    font-size: 12px;
    color: #333;
  }
  input, select {
    width: 90%;
    padding: 8px;
    font-size: 1em;
    border: 2px solid ;
    border-radius: 4px;
    background: #fff;
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1em;
  margin-bottom: 1em;
  label {
    display: flex;
    align-items: center;
    font-size: 16px;
    color: #333;
    font-weight: bolder;
    margin-right: 1em;
  }
  input { margin-right: 0.5em; } 
`;

const DateSelect = styled.div`
  display: flex;
  gap: 0.5em;
  select { flex: 1; }
`;

const SubmitRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1em;
  margin-top: 2em;
  button {
    padding: 0.6em 1.2em;
    font-size: 1em;
    border: none;
    border-radius: 4px;
    cursor: pointer;
  }
  .back {
    background: ${({ colors }) => colors.cardBackground};
    color: ${({ colors }) => colors.labelText};
    border: 1px solid ${({ colors }) => colors.border};
  }
  .submit {
    background: ${({ colors }) => colors.buttonBackground};
    color: white;
    &:hover {
      background: ${({ colors }) => colors.buttonHoverBackground};
    }
  }
`;

const ISAFE_ROLES = new Set([
  "isafe_accountant",
  "isafe_moderator",
  "isafe_salesperson",
  "isafe_admin",
  "root",
]);
const SUBSCRIPTION_ROLES = new Set([
  "subscription_access",
  "subscription_accountant",
  "subscription_admin",
  "subscription_implementor",
  "subscription_salesperson",
]);

const padTwoDigits = (value) => String(value).padStart(2, "0");

const parseDateParts = (value) => {
  if (!value || typeof value !== "string") return null;
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  return { year: match[1], month: match[2], day: match[3] };
};

const partsToDate = (parts) => {
  if (!parts) return null;
  const year = Number(parts.year);
  const month = Number(parts.month);
  const day = Number(parts.day);
  if ([year, month, day].some((n) => Number.isNaN(n))) return null;
  return new Date(year, month - 1, day);
};

const dateToParts = (date) =>
  date
    ? {
        year: String(date.getFullYear()),
        month: padTwoDigits(date.getMonth() + 1),
        day: padTwoDigits(date.getDate()),
      }
    : null;

const clampDateParts = (parts, minDate, maxDate) => {
  if (!parts) return parts;
  const candidate = partsToDate(parts);
  if (!candidate || Number.isNaN(candidate.getTime())) return parts;
  let clamped = candidate;
  if (minDate && clamped < minDate) clamped = new Date(minDate);
  if (maxDate && clamped > maxDate) clamped = new Date(maxDate);
  return dateToParts(clamped);
};

const areDatePartsEqual = (a, b) =>
  Boolean(a && b && a.year === b.year && a.month === b.month && a.day === b.day);

const buildYearOptions = (minYear, maxYear, descending = true) => {
  if (minYear == null && maxYear == null) return [];
  const actualMin = minYear != null ? minYear : maxYear;
  const actualMax = maxYear != null ? maxYear : minYear;
  if (actualMin == null || actualMax == null) return [];
  const lower = Math.min(actualMin, actualMax);
  const upper = Math.max(actualMin, actualMax);
  const years = [];
  for (let year = lower; year <= upper; year += 1) {
    years.push(year);
  }
  return descending ? years.reverse() : years;
};

export default function AddTeacherForm({ colors, onCancel, onAdded }) {
  const gradeKeys = ["K","1","2","3","4","5","6","7","8","9","10","11","12"];
  const current   = useSelector(s => s.auth.selected_subscriptions);
  const schoolsCache = useSelector(s => s.auth.subscription_schools || {});
  const cachedSchoolLevels = useSelector(s => s.auth.school_levels);
  const roles = useSelector((s) => s.auth.user.roles || []);
  const become = useSelector((s) => s.auth.become);
  const dispatch = useDispatch();

  const normalizeId = value =>
    value === undefined || value === null ? "" : String(value).trim();

  const normalizeSchool = school => {
    if (!school || typeof school !== "object") return school;
    const orgId =
      normalizeId(school.org_id ?? school.school_id ?? school.orgId ?? school.orgID);
    const display =
      school.org_display ??
      school.org_name ??
      school.school_name ??
      school.name ??
      "";
    const name =
      school.org_name ??
      school.org_display ??
      school.school_name ??
      school.name ??
      display;
    return {
      ...school,
      org_id: orgId,
      org_display: display,
      org_name: name,
    };
  };

  const dedupeSchools = list => {
    const seen = new Set();
    return list.filter(item => {
      const id = normalizeId(item?.org_id ?? item?.school_id);
      if (!id || seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  };

  const normalizeSchools = list =>
    dedupeSchools(Array.isArray(list) ? list.map(normalizeSchool) : []);

  const normalizeLevel = level => {
    if (!level || typeof level !== "object") return level;
    const code = normalizeId(level.school_level);
    const desc = typeof level.school_level_desc === "string"
      ? level.school_level_desc.trim()
      : level.school_level_desc;
    return {
      ...level,
      school_level: code,
      school_level_desc: desc ?? code,
    };
  };

  const normalizeLevels = list => {
    const seen = new Set();
    return (Array.isArray(list) ? list.map(normalizeLevel) : []).filter(level => {
      const code = normalizeId(level?.school_level);
      if (!code || seen.has(code)) return false;
      seen.add(code);
      return true;
    });
  };

  // date helpers
  const today = new Date();
  const currentYear = today.getFullYear();
  const maxFutureYears = 20;
  const hasISAFERole = roles.some((role) => ISAFE_ROLES.has(role.id));
  const hasSubscriptionAdminRole = roles.some((role) => SUBSCRIPTION_ROLES.has(role.id));
  const allowEndDateBackdate =
    become === 1 && hasISAFERole && !hasSubscriptionAdminRole;
  const isSubscriptionAdmin = hasSubscriptionAdminRole;
  const subscriptionStartRaw = current?.start_date || current?.start;
  const subscriptionEndRaw = current?.end_date || current?.end;
  const subscriptionStartParts = parseDateParts(subscriptionStartRaw);
  const subscriptionEndParts = parseDateParts(subscriptionEndRaw);
  const subscriptionStartDate = partsToDate(subscriptionStartParts);
  const subscriptionEndDate = partsToDate(subscriptionEndParts);
  const daysInMonth = (y, m) => new Date(y, m, 0).getDate();
  const startYearMin = subscriptionStartDate
    ? subscriptionStartDate.getFullYear()
    : Math.max(currentYear - 10, 0);
  const startYearMax = subscriptionEndDate
    ? subscriptionEndDate.getFullYear()
    : currentYear;
  const fallbackStartYears = Array.from({ length: 11 }, (_, i) => currentYear - i);
  const startYearOptions = buildYearOptions(startYearMin, startYearMax);
  const fallbackEndMin = allowEndDateBackdate ? 2022 : currentYear;
  const endYearMin = subscriptionStartDate
    ? subscriptionStartDate.getFullYear()
    : fallbackEndMin;
  const endYearMax = subscriptionEndDate
    ? subscriptionEndDate.getFullYear()
    : currentYear + maxFutureYears;
  const lastEndYear = currentYear + maxFutureYears;
  const earliestEndYear = fallbackEndMin;
  const fallbackEndYears = Array.from(
    { length: Math.max(lastEndYear - earliestEndYear + 1, 1) },
    (_, i) => earliestEndYear + i
  );
  const endYearOptions = buildYearOptions(endYearMin, endYearMax);
  const effectiveStartYears =
    isSubscriptionAdmin && startYearOptions.length
      ? startYearOptions
      : fallbackStartYears;
  const effectiveEndYears =
    isSubscriptionAdmin && endYearOptions.length
      ? endYearOptions
      : fallbackEndYears;

  // form state
  const [sendEmailToTeacher, setSendEmailToTeacher] = useState(false);
  const [sendEmailToAdmin,   setSendEmailToAdmin]   = useState(false);
  const [schools, setSchools]               = useState([]);
  const [schoolLevels, setSchoolLevels]     = useState([]);
  const [schoolLevel, setSchoolLevel]       = useState("");
  const [firstName, setFirstName]           = useState("");
  const [lastName, setLastName]             = useState("");
  const [email, setEmail]                   = useState("");
  const [schoolId, setSchoolId]             = useState("");
  const [selectedGrades, setSelectedGrades] = useState(
    gradeKeys.reduce((acc, g) => ({ ...acc, [g]: false }), {})
  );
  const [students, setStudents] = useState("");
  const defaultStartDate = subscriptionStartParts
    ? { ...subscriptionStartParts }
    : {
        year: String(currentYear),
        month: padTwoDigits(today.getMonth() + 1),
        day: padTwoDigits(today.getDate()),
      };
  const defaultEndDate = subscriptionEndParts
    ? { ...subscriptionEndParts }
    : subscriptionStartParts
    ? { ...subscriptionStartParts }
    : { ...defaultStartDate };
  const [startDate, setStartDate] = useState(() => ({ ...defaultStartDate }));
  const [endDate, setEndDate] = useState(() => ({ ...defaultEndDate }));
  const startDateValue = partsToDate(startDate);
  const endDateValue = partsToDate(endDate);
  const updateStartDate = isSubscriptionAdmin
    ? (changes) =>
        setStartDate((prev) =>
          clampDateParts(
            { ...prev, ...changes },
            subscriptionStartDate,
            subscriptionEndDate
          )
        )
    : (changes) => setStartDate((prev) => ({ ...prev, ...changes }));
  const updateEndDate = isSubscriptionAdmin
    ? (changes) => {
        const minDate = startDateValue || subscriptionStartDate;
        setEndDate((prev) =>
          clampDateParts({ ...prev, ...changes }, minDate, subscriptionEndDate)
        );
      }
    : (changes) => setEndDate((prev) => ({ ...prev, ...changes }));

  useEffect(() => {
    if (!isSubscriptionAdmin || (!subscriptionStartRaw && !subscriptionEndRaw)) {
      return;
    }
    const min = subscriptionStartDate;
    const max = subscriptionEndDate;
    let nextStartParts = null;
    setStartDate((prev) => {
      const clamped = clampDateParts(prev, min, max);
      nextStartParts = clamped;
      return areDatePartsEqual(clamped, prev) ? prev : clamped;
    });
    setEndDate((prev) => {
      const minEnd = nextStartParts ? partsToDate(nextStartParts) : min;
      const clampedEnd = clampDateParts(prev, minEnd, max);
      return areDatePartsEqual(clampedEnd, prev) ? prev : clampedEnd;
    });
  }, [
    isSubscriptionAdmin,
    subscriptionStartRaw,
    subscriptionEndRaw,
    subscriptionStartDate,
    subscriptionEndDate,
  ]);

  // confirm/result modal state
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [resultOpen, setResultOpen]   = useState(false);
  const [modalTitle, setModalTitle]   = useState("");
  const [modalMsg, setModalMsg]       = useState("");
  const [modalStatus, setModalStatus] = useState("Success");
  const [payload, setPayload]         = useState(null);

  const lastSchoolsFetchKey = useRef("");
  const stateCode = current?.org_state;

  // hydrate schools from cache
  useEffect(() => {
    if (!stateCode) {
      setSchools([]);
      return;
    }

    if (Object.prototype.hasOwnProperty.call(schoolsCache, stateCode)) {
      setSchools(normalizeSchools(schoolsCache[stateCode]));
    }
  }, [stateCode, schoolsCache]);

  // refetch once per subscription/state to keep redux fresh
  useEffect(() => {
    if (!stateCode || !current?.invoice_id) return;
    const fetchKey = `${current.invoice_id}|${stateCode}`;
    if (lastSchoolsFetchKey.current === fetchKey) return;
    lastSchoolsFetchKey.current = fetchKey;

    let cancelled = false;
    API.getSubscriptionSchools({ state: stateCode })
      .then(resp => {
        if (cancelled) return;
        const list = normalizeSchools(resp?.schools);
        setSchools(list);
        dispatch({
          type: actions.STORE_SUBSCRIPTION_SCHOOLS,
          schoolsByState: { [stateCode]: list },
        });
      })
      .catch(err => {
        if (!cancelled) {
          console.error("Failed to load schools:", err);
          setSchools([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [stateCode, current?.invoice_id, dispatch]);

  // hydrate school levels from cache (fallback to API when missing)
  useEffect(() => {
    if (Array.isArray(cachedSchoolLevels)) {
      setSchoolLevels(normalizeLevels(cachedSchoolLevels));
      return;
    }

    let cancelled = false;
    API.getSchoolLevels()
      .then(resp => {
        if (cancelled) return;
        const levels = normalizeLevels(resp?.school_levels);
        setSchoolLevels(levels);
        dispatch({
          type: actions.STORE_SCHOOL_LEVELS,
          schoolLevels: levels,
        });
      })
      .catch(err => {
        if (!cancelled) {
          console.error("Failed to load school levels:", err);
          setSchoolLevels([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [cachedSchoolLevels, dispatch]);

  const handleGradeToggle = g =>
    setSelectedGrades(gs => ({ ...gs, [g]: !gs[g] }));

  const handleSelectAll = () => {
    setSelectedGrades(gradeKeys.reduce((acc,g)=>(acc[g]=true,acc), {}));
  };

  const buildPayload = () => {
    const selectedSchool =
      schools?.find(s => String(s.org_id) === String(schoolId)) || {};
    const resolvedOrgId = selectedSchool.org_id ?? schoolId;
    const resolvedOrgNameRaw =
      selectedSchool.org_name || selectedSchool.org_display || "";
    const resolvedOrgName =
      typeof resolvedOrgNameRaw === "string"
        ? resolvedOrgNameRaw.trim()
        : resolvedOrgNameRaw;

    return {
      customer_id:          current.customer_id,
      email,
      num_of_students:      students,
      first_name:           firstName,
      last_name:            lastName,
      invoice_id:           current.invoice_id,
      school_id:            schoolId,
      school_level:         schoolLevel,
      org_id:               resolvedOrgId,
      org_name:             resolvedOrgName,
      start_date:           `${startDate.year}-${startDate.month}-${startDate.day}`,
      end_date:             `${endDate.year}-${endDate.month}-${endDate.day}`,
      grades:               gradeKeys.filter(g => selectedGrades[g]),
      send_email_to_teacher: sendEmailToTeacher ? 1 : 0,
      send_email_to_admin:   sendEmailToAdmin   ? 1 : 0,
    };
  };

  const handleSubmit = e => {
    e.preventDefault();
    const p = buildPayload();
    if (isSubscriptionAdmin && subscriptionStartDate && subscriptionEndDate) {
      if (
        !startDateValue ||
        !endDateValue ||
        startDateValue < subscriptionStartDate ||
        endDateValue > subscriptionEndDate ||
        endDateValue < startDateValue
      ) {
        showErrorModal("Implementation start and end dates must fall within the subscription timeline.");
        return;
      }
    }
    if (!p.first_name || !p.last_name || !p.email ||
        !p.school_id  || !p.school_level || !p.grades.length ||
        !p.start_date || !p.end_date || !p.num_of_students) {
      showErrorModal("Please fill out all required fields.");
      return;
    }
    setPayload(p);
    setModalTitle("Add Teacher");
    setModalMsg("Are you sure you want to add this teacher?");
    setModalStatus("Success");
    setConfirmOpen(true);
  };

  const handleConfirm = () => {
    setConfirmOpen(false);
    API.addSubscriptionTeacher(payload)
      .then(resp => {
        const status = resp?.status === "Failed" ? "Failed" : "Success";
        setModalTitle(status === "Failed" ? "Error" : "Success");
        setModalMsg(resp.message || (status === "Failed" ? "Failed to add teacher." : "Teacher added."));
        setModalStatus(status);
        setResultOpen(true);
      })
      .catch(err => {
        setModalTitle("Error");
        setModalMsg(err.message || "Failed to add teacher.");
        setModalStatus("Failed");
        setResultOpen(true);
      });
  };

  const handleResultClose = () => {
    setResultOpen(false);
    setModalStatus("Success");
    onAdded();
  };

  return (
    <Container>
      <form onSubmit={handleSubmit}>
        <SectionTitle colors={colors}>Teacher Information</SectionTitle>

        <FieldRow>
          <CheckboxGroup colors={colors}>
            <label>
              <input
                type="checkbox"
                checked={sendEmailToTeacher}
                onChange={e => setSendEmailToTeacher(e.target.checked)}
              />
              Send auto e-mail notification to this educator?
            </label>
            <label>
              <input
                type="checkbox"
                checked={sendEmailToAdmin}
                onChange={e => setSendEmailToAdmin(e.target.checked)}
              />
              Send auto e-mail notification to administrator?
            </label>
          </CheckboxGroup>
        </FieldRow>

        <FieldRow>
          <Field colors={colors}>
            <label htmlFor="firstName">First Name</label>
            <input
              id="firstName"
              type="text"
              value={firstName}
              onChange={e => setFirstName(e.target.value)}
              required
            />
          </Field>
          <Field colors={colors}>
            <label htmlFor="lastName">Last Name</label>
            <input
              id="lastName"
              type="text"
              value={lastName}
              onChange={e => setLastName(e.target.value)}
              required
            />
          </Field>
        </FieldRow>

        <FieldRow>
          <Field colors={colors}>
            <label htmlFor="email">E-mail</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
            />
          </Field>
        </FieldRow>

        <SectionTitle colors={colors}>School Information</SectionTitle>
        <FieldRow>
          <Field colors={colors}>
            <label>School</label>
            <select
              value={schoolId}
              style={{padding: 10}}
              onChange={e => setSchoolId(e.target.value)}
              required
            >
              <option value="">— select school —</option>
              {schools?.map(s => (
                <option key={s.org_id} value={s.org_id}>
                  {s.org_display}
                </option>
              ))}
            </select>
          </Field>
        </FieldRow>
        <FieldRow>
          <Field colors={colors}>
            <label>School Level</label>
            <select
              value={schoolLevel}
              onChange={e => setSchoolLevel(e.target.value)}
              required
            >
              <option value="">— select school level —</option>
              {schoolLevels?.map(level => {
                const label = level.school_level_desc || level.school_level;
                return (
                  <option key={level.school_level} value={level.school_level}>
                    {label}
                  </option>
                );
              })}
            </select>
          </Field>
        </FieldRow>

        <SectionTitle colors={colors}>Implementation Information</SectionTitle>
        <Field colors={colors}>
          <div style={{ display: "flex", alignItems: "center" }}>
            <label>Grades</label>
            <span
              onClick={handleSelectAll}
              style={{
                marginLeft: "1em",
                fontSize: "0.9em",
                cursor: "pointer",
                color: '#E09723',
                textDecoration: "underline",
                fontWeight: 'bold'
              }}
            >
              Select All
            </span>
          </div>
          <CheckboxGroup colors={colors}>
            {gradeKeys?.map(g => (
              <label key={g}>
                <input
                  type="checkbox"
                  checked={selectedGrades[g]}
                  onChange={() => handleGradeToggle(g)}
                />
                {g}
              </label>
            ))}
          </CheckboxGroup>
        </Field>

        <FieldRow>
          <Field colors={colors}>
            <label>Number of Students</label>
            <input
              type="text"
              value={students}
              onChange={e => setStudents(e.target.value)}
              required
            />
          </Field>
        </FieldRow>

        <FieldRow>
          <Field colors={colors}>
            <label>Start Date</label>
            <DateSelect>
              {/* last 10 years back */}
              <select
                value={startDate.month}
                onChange={e => updateStartDate({ month: e.target.value })}
              >
                {Array.from({ length: 12 }, (_, i) => i + 1).map(m => {
                  const mm = String(m).padStart(2, "0");
                  return <option key={mm} value={mm}>{mm}</option>;
                })}
              </select>
              <select
                value={startDate.day}
                onChange={e => updateStartDate({ day: e.target.value })}
              >
                {Array.from(
                  { length: daysInMonth(startDate.year, startDate.month) },
                  (_, i) => i + 1
                ).map(d => {
                  const dd = String(d).padStart(2, "0");
                  return <option key={dd} value={dd}>{dd}</option>;
                })}
              </select>
              <select
                value={startDate.year}
                onChange={e => updateStartDate({ year: e.target.value })}
              >
                {effectiveStartYears.map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </DateSelect>
          </Field>

          <Field colors={colors}>
            <label>End Date</label>
            <DateSelect>
              <select
                value={endDate.month}
                onChange={e => updateEndDate({ month: e.target.value })}
              >
                {Array.from({ length: 12 }, (_, i) => i + 1).map(m => {
                  const mm = String(m).padStart(2, "0");
                  return <option key={mm} value={mm}>{mm}</option>;
                })}
              </select>
              <select
                value={endDate.day}
                onChange={e => updateEndDate({ day: e.target.value })}
              >
                {Array.from(
                  { length: daysInMonth(endDate.year, endDate.month) },
                  (_, i) => i + 1
                ).map(d => {
                  const dd = String(d).padStart(2, "0");
                  return <option key={dd} value={dd}>{dd}</option>;
                })}
              </select>
              <select
                value={endDate.year}
                onChange={e => updateEndDate({ year: e.target.value })}
              >
                {effectiveEndYears.map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </DateSelect>
          </Field>
        </FieldRow>

        <SubmitRow colors={colors}>
          <button type="button" className="back" onClick={onCancel}>
            Back
          </button>
          <button type="submit" className="submit">
            Submit
          </button>
        </SubmitRow>
      </form>

      <ConfirmationModal
        show={confirmOpen}
        title={modalTitle}
        message={modalMsg}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={handleConfirm}
      />

      <ConfirmationModal
        show={resultOpen}
        title={modalTitle}
        message={modalMsg}
        onCancel={handleResultClose}
        onConfirm={handleResultClose}
        status={modalStatus}
        single
      />
    </Container>
  );
}
